import { TableRow, type DataTableColumn } from "@carbon6/c6-frontend-design";
import { useCallback, useEffect, useMemo, type ReactNode } from "react";
import {
  listAdminUploadJobChunks,
  type AdminUploadChunk,
  type UploadChunkPhase,
} from "@/api/adminUploadJob";
import { HatDataTable } from "@/components/table/HatDataTable";
import { StaticTableSource } from "@/components/table/StaticTableSource";
import { EmptyState, stateFrameClassName } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { usePolledRequest } from "./usePolledRequest";
import detailStyles from "./WorkerDetailContent.module.css";
import styles from "./UploadChunkTable.module.css";

const PHASE_LABELS: Record<UploadChunkPhase, string> = {
  pending: "대기",
  copying: "복사 중",
  copied: "복사 완료",
  committed: "확정 완료",
};

// c6 DataTableColumn.key 는 행 프로퍼티만 받는다. 그래서 컬럼 키를 게터로 선언한다.
class UploadChunkRow extends TableRow {
  constructor(readonly chunk: AdminUploadChunk) {
    super(null);
  }
  get ordinal() {
    return this.chunk.ordinal;
  }
  get phase() {
    return this.chunk.phase;
  }
  get episodes() {
    return this.chunk.episode_count;
  }
  get copied() {
    return this.chunk.copied;
  }
  get committed() {
    return this.chunk.committed;
  }
  getRowId() {
    return String(this.chunk.ordinal);
  }
  canShow() {
    return true;
  }
  canSelect() {
    return false;
  }
}

const COLUMNS: readonly DataTableColumn<UploadChunkRow>[] = [
  { key: "ordinal", header: "청크", width: "88px", render: (row) => `#${row.ordinal}` },
  { key: "phase", header: "단계", width: "120px", render: (row) => PHASE_LABELS[row.phase] },
  { key: "episodes", header: "에피소드", width: "100px", render: (row) => row.episodes },
  {
    key: "copied",
    header: "복사",
    width: "minmax(120px, 1fr)",
    render: (row) => `${row.copied} / ${row.episodes}`,
  },
  {
    key: "committed",
    header: "확정",
    width: "minmax(120px, 1fr)",
    render: (row) => `${row.committed} / ${row.episodes}`,
  },
];

/**
 * 업로드 작업의 청크 표다. Worker 하나가 청크를 차례로 복사하고 확정한다. 청크 수는 에피소드 수를
 * 청크 크기로 나눈 값이다. 그래서 이 표는 페이지를 나누지 않는다. 이 표는 마운트된 동안 5초마다
 * 청크를 다시 받는다.
 */
export function UploadChunkTable({ jobUuid }: { jobUuid: string }): ReactNode {
  const request = useCallback(() => listAdminUploadJobChunks(jobUuid), [jobUuid]);
  const { data, error } = usePolledRequest(request);
  const source = useMemo(
    () => new StaticTableSource((data?.items ?? []).map((chunk) => new UploadChunkRow(chunk))),
    [data],
  );
  useEffect(() => () => source.dispose(), [source]);
  return (
    <section className={detailStyles.section} aria-label="청크">
      <h3 className={detailStyles.sectionTitle}>청크</h3>
      <div className={styles.body}>
        {data === null ? (
          // 표 틀 안에 둔다. 높이는 표 틀이 정한다. 그래서 청크가 도착해도 섹션 높이가 바뀌지 않는다.
          <div className={detailStyles.table}>
            <div className={stateFrameClassName}>
              {error === null ? (
                <LoadingState mode="plain">청크를 불러오고 있습니다.</LoadingState>
              ) : (
                <EmptyState mode="plain" tone="error">
                  {error}
                </EmptyState>
              )}
            </div>
          </div>
        ) : (
          <>
            {error !== null && (
              <p role="alert" className={detailStyles.error}>
                {error}
              </p>
            )}
            <p className={styles.note}>
              청크 하나에 에피소드가 최대 {data.chunk_episodes}개 있습니다.
            </p>
            <div className={detailStyles.table}>
              <HatDataTable
                source={source}
                columns={COLUMNS}
                ariaLabel="청크 테이블"
                emptyMessage="아직 청크가 없습니다. 원본 목록을 만든 뒤에 에피소드 수에 맞춰 청크를 나눕니다."
                minWidth="640px"
                showIndex={false}
                scrollable
              />
            </div>
          </>
        )}
      </div>
    </section>
  );
}
